import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReconciliationLine, ReconciliationSession } from '../types'
import { LineStatusBadge } from './StatusBadge'
import { XmlDiffView } from './XmlDiffView'

function fmt(val: number | null): string {
  if (val === null) return '—'
  if (val % 1 === 0) return String(val)
  return parseFloat(val.toFixed(4)).toString()
}

function fmtPrice(val: number | null): string {
  if (val === null) return '—'
  const stripped = parseFloat(val.toFixed(5)).toString()
  const [int, dec = ''] = stripped.split('.')
  return `€ ${int}.${dec.padEnd(2, '0')}`
}

function delta(a: number | null, b: number | null): string | null {
  if (a === null || b === null) return null
  const d = b - a
  if (Math.abs(d) < 0.001) return null
  const sign = d > 0 ? '+' : ''
  return `${sign}€ ${d.toFixed(2)}`
}

function deltaPct(a: number | null, b: number | null): string | null {
  if (a === null || b === null || a === 0) return null
  const d = ((b - a) / a) * 100
  if (Math.abs(d) < 0.01) return null
  return `${d > 0 ? '+' : ''}${d.toFixed(1)}%`
}

interface RowProps {
  label: string
  or: React.ReactNode
  da?: React.ReactNode
  inv: React.ReactNode
  highlight?: boolean
}

function CompareRow({ label, or, da, inv, highlight }: RowProps) {
  return (
    <tr className={highlight ? 'bg-red-50/60 dark:bg-red-900/20' : 'even:bg-gray-50/40 dark:even:bg-gray-900/40'}>
      <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">{label}</td>
      <td className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 text-right tabular-nums">{or}</td>
      {da !== undefined && (
        <td className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 text-right tabular-nums">{da}</td>
      )}
      <td className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 text-right tabular-nums">{inv}</td>
    </tr>
  )
}

type Tab = 'compare' | 'diff'

interface Props {
  line: ReconciliationLine
  session?: ReconciliationSession
  onClose: () => void
}

export function LineDetailPanel({ line, session, onClose }: Props) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<Tab>('compare')

  const hasDesadv = line.desadv_qty !== null
  const hasOrdrsp = line.ordrsp_qty !== null

  const hasDiffTab = !!session && (
    (!!session.ordrsp_filename && !!line.ordrsp_xpath) ||
    (!!session.desadv_filename && !!line.desadv_xpath) ||
    (!!session.invoice_filename && !!line.invoice_xpath)
  )

  const qtyMismatch = line.desadv_qty !== null && line.invoice_qty !== null && line.desadv_qty !== line.invoice_qty
  const priceMismatch = line.ordrsp_price !== null && line.invoice_price !== null
    && Math.abs(line.ordrsp_price - line.invoice_price) > 0.01
  const priceListMismatch = line.status === 'PRICE_LIST_MISMATCH'

  const invDelta    = delta(line.ordrsp_price, line.invoice_price)
  const invDeltaPct = deltaPct(line.ordrsp_price, line.invoice_price)
  const agrDelta    = delta(line.agreed_net_price, line.invoice_price)
  const agrDeltaPct = deltaPct(line.agreed_net_price, line.invoice_price)

  return (
    <div className="border border-indigo-200 dark:border-indigo-800/50 rounded-xl bg-white dark:bg-gray-800 shadow-md overflow-hidden animate-fadeSlideIn">

      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-100 dark:border-indigo-800/50">
        <div className="space-y-0.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-indigo-900 dark:text-indigo-300">
              {line.article_number.startsWith('[?') ? (
                <span className="text-orange-600 italic">{t('line_detail_panel.no_article_number')}</span>
              ) : line.article_number}
            </span>
            {line.gtin && (
              <span className="text-xs font-mono text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5">
                GTIN: {line.gtin}
              </span>
            )}
            <LineStatusBadge status={line.status} />
          </div>
          {line.description && (
            <p className="text-xs text-gray-600 dark:text-gray-400">{line.description}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none transition-colors"
          aria-label={t('line_detail_panel.close_aria_label')}
        >
          ×
        </button>
      </div>

      {/* Tabs — alleen zichtbaar als XML diff beschikbaar is */}
      {hasDiffTab && (
        <div className="flex border-b border-gray-100 dark:border-gray-800 px-4 bg-white dark:bg-gray-800">
          {(['compare', 'diff'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors mr-1 ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-700 dark:text-indigo-300'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'compare' ? t('line_detail_panel.tab_compare') : t('line_detail_panel.tab_diff')}
            </button>
          ))}
        </div>
      )}

      {/* XML Diff tab */}
      {activeTab === 'diff' && session && (
        <div className="p-4">
          <XmlDiffView session={session} line={line} />
        </div>
      )}

      {/* Vergelijking tab */}
      {activeTab === 'compare' && (
        <div className="p-4 space-y-4">

          {/* Status-uitleg */}
          {line.status !== 'OK' && line.status_details && (
            <div className={`rounded-lg px-3 py-2 text-xs ${
              line.status === 'UOM_CONVERSION'
                ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 text-blue-800 dark:text-blue-300'
                : line.status === 'NO_ARTICLE_NUMBER'
                  ? 'bg-orange-50 border border-orange-100 text-orange-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 text-red-800 dark:text-red-300'
            }`}>
              <span className="font-semibold">{t('line_detail_panel.status_reason_label')} </span>{line.status_details}
            </div>
          )}

          {/* Vergelijkingstabel */}
          <div>
            <div className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
              {t('line_detail_panel.quantities_and_prices_heading')}
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-3 py-1.5 text-left text-gray-500 dark:text-gray-400 font-medium text-[10px] uppercase tracking-wide w-28">{t('line_detail_panel.column_field')}</th>
                    {hasOrdrsp && (
                      <th className="px-3 py-1.5 text-right text-blue-600 font-medium text-[10px] uppercase tracking-wide">OR</th>
                    )}
                    {hasDesadv && (
                      <th className="px-3 py-1.5 text-right text-emerald-600 font-medium text-[10px] uppercase tracking-wide">DA</th>
                    )}
                    <th className="px-3 py-1.5 text-right text-violet-600 font-medium text-[10px] uppercase tracking-wide">{t('line_detail_panel.column_invoice')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  <CompareRow
                    label={`${t('line_detail_panel.row_quantity')}${line.unit ? ` (${line.unit})` : ''}`}
                    or={hasOrdrsp ? fmt(line.ordrsp_qty) : '—'}
                    da={hasDesadv ? (
                      <span className={qtyMismatch ? 'text-red-700 font-medium' : ''}>{fmt(line.desadv_qty)}</span>
                    ) : undefined}
                    inv={<span className={qtyMismatch ? 'text-red-700 font-medium' : ''}>{fmt(line.invoice_qty)}</span>}
                    highlight={qtyMismatch}
                  />
                  {line.invoice_invoicing_units !== null && (
                    <CompareRow
                      label={`${t('line_detail_panel.row_invoicing_units')}${line.price_basis_uom ? ` (${line.price_basis_uom})` : ''}`}
                      or="—"
                      da={hasDesadv ? '—' : undefined}
                      inv={`${fmt(line.invoice_invoicing_units)}${line.price_basis_uom ? ` ${line.price_basis_uom}` : ''}`}
                    />
                  )}
                  <CompareRow
                    label={t('line_detail_panel.row_price')}
                    or={hasOrdrsp ? fmtPrice(line.ordrsp_price) : '—'}
                    da={hasDesadv ? '—' : undefined}
                    inv={(
                      <span className={priceMismatch ? 'text-red-700 font-medium' : ''}>
                        {line.price_basis_uom
                          ? `${fmtPrice(line.invoice_price)}/${line.price_basis_uom}`
                          : fmtPrice(line.invoice_price)
                        }
                      </span>
                    )}
                    highlight={priceMismatch}
                  />
                  {invDelta && (
                    <tr className="bg-red-50/40">
                      <td className="px-3 py-1 text-[10px] text-red-500 italic" colSpan={hasDesadv ? 4 : 3}>
                        {t('line_detail_panel.price_deviation_vs_or', { delta: invDelta })}
                        {invDeltaPct && <span className="ml-1.5 text-red-400">({invDeltaPct})</span>}
                      </td>
                    </tr>
                  )}
                  <CompareRow
                    label={t('line_detail_panel.row_net_amount')}
                    or={hasOrdrsp ? fmtPrice(line.ordrsp_net_amount) : '—'}
                    da={hasDesadv ? '—' : undefined}
                    inv={fmtPrice(line.invoice_net_amount)}
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* Prijslijst */}
          {(line.agreed_gross_price !== null || line.agreed_net_price !== null) && (
            <div>
              <div className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                {t('line_detail_panel.price_list_heading')}
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full text-xs border-collapse">
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    <tr className="even:bg-gray-50/40 dark:even:bg-gray-900/40">
                      <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400 font-medium w-28">{t('line_detail_panel.gross_price_label')}</td>
                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 text-right tabular-nums italic">{fmtPrice(line.agreed_gross_price)}</td>
                    </tr>
                    <tr className="even:bg-gray-50/40 dark:even:bg-gray-900/40">
                      <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400 font-medium">{t('line_detail_panel.net_price_label')}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums italic ${priceListMismatch ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                        {fmtPrice(line.agreed_net_price)}
                        {agrDelta && (
                          <span className="ml-2 font-normal text-amber-600">
                            {agrDelta}
                            {agrDeltaPct && <span className="ml-1 text-amber-500">({agrDeltaPct})</span>}
                          </span>
                        )}
                      </td>
                    </tr>
                    {line.price_list_name && (
                      <tr className="even:bg-gray-50/40 dark:even:bg-gray-900/40">
                        <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400 font-medium">{t('line_detail_panel.price_list_label')}</td>
                        <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400 text-right">
                          {line.price_list_name}
                          {line.price_list_source_format && (
                            <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5">
                              {line.price_list_source_format}
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                    {line.not_in_price_list && (
                      <tr>
                        <td colSpan={2} className="px-3 py-1.5 text-amber-700 dark:text-amber-300 text-xs">
                          {t('line_detail_panel.not_in_price_list_warning')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* BTW */}
          {line.vat_percentage !== null && (
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">{t('line_detail_panel.vat_rate_label')}</span>
              <span className="font-mono">{line.vat_percentage}%</span>
              {line.invoice_net_amount !== null && (
                <>
                  <span className="text-gray-300">|</span>
                  <span>{t('line_detail_panel.vat_amount_label')} {fmtPrice(line.invoice_net_amount * line.vat_percentage / 100)}</span>
                </>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  )
}
